import { forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import BillClientDetails from '@hub/features/purchases/components/bill/bill-client-details';
import BillCompanyDetails from '@hub/features/purchases/components/bill/bill-company-details';
import BillHeader from '@hub/features/purchases/components/bill/bill-header';
import InvoicePaymentDetails from '@hub/features/purchases/orders/preview-invoice-modal/preview-invoice/invoice-payment-details/invoice-payment-details';
import InvoiceTable from '@hub/features/purchases/orders/preview-invoice-modal/preview-invoice/invoice-table/invoice-table';
import { formatDate } from '@monorepo/client-common';
import { designTokens, Divider, Stack, Typography } from '@monorepo/nova-ui';
import { InvoiceDisplayData } from '@monorepo/types';

interface HiddenInvoiceProps {
  invoice: InvoiceDisplayData;
}

/**
 * Hidden invoice component for PDF generation.
 *
 * This component renders the complete invoice layout in a hidden container
 * that can be referenced for PDF generation. It reuses all existing invoice
 * components to maintain consistency with the preview invoice page.
 *
 * @example
 * ```tsx
 * const invoiceRef = useRef();
 *
 * return (
 *   <HiddenInvoice
 *     ref={invoiceRef}
 *     invoice={invoiceData}
 *   />
 * );
 * ```
 */
export const HiddenInvoice = forwardRef<HTMLDivElement, HiddenInvoiceProps>(
  ({ invoice }, ref) => {
    const { t } = useTranslation(['orders']);

    const { companyDetails, purchase } = invoice;
    const { updatedAt, billingInformation, providerData, snapshotId } =
      purchase;

    return (
      <div
        ref={ref}
        className="preview-invoice"
        style={{
          position: 'fixed',
          left: '-9999px',
          top: '-9999px',
          width: '210mm', // A4 width
          height: '297mm', // A4 height
          backgroundColor: 'white',
          padding: '20px',
          fontFamily: 'Arial, sans-serif',
          zIndex: 9999,
          overflow: 'hidden',
        }}
      >
        <Stack gap={4}>
          <BillHeader
            uniqId={purchase.purchaseId}
            snapshotId={snapshotId}
            title={t('orders:invoice')}
          />
          <BillCompanyDetails
            name={companyDetails.companyName}
            city={companyDetails.city}
            zipCode={companyDetails.zipCode}
            country={companyDetails.country}
            title={companyDetails.companyTitle}
            street={companyDetails.street}
          />
          <Divider />
          <BillClientDetails
            title={t('orders:preview_invoice.invoice_to')}
            firstName={billingInformation.firstName}
            lastName={billingInformation.lastName}
            address={billingInformation.billingAddress.address}
            phoneNumber={billingInformation.phoneNumber}
            email={billingInformation.email}
          />
          <InvoiceTable invoiceData={invoice} />
          <InvoicePaymentDetails
            providerData={providerData}
            updatedAt={formatDate(updatedAt, { format: 'YYYY/MM/DD HH:mm' })}
          />
          {providerData.transactionId && (
            <>
              <Divider />
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="caption" color="text.secondary">
                  {t('orders:preview_invoice.transaction_id')}
                </Typography>
                <Typography
                  variant="body2"
                  fontWeight={designTokens.typography.fontWeights.medium}
                >
                  {providerData.transactionId}
                </Typography>
              </Stack>
            </>
          )}
        </Stack>
      </div>
    );
  },
);
